import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../utils/config";
import { pageTitleFor } from "../utils/pageTitles";
import {
  isPushSupported,
  getExistingSubscription,
  subscribeToPush,
  unsubscribeFromPush,
} from "../utils/pushNotifications";

const NOTIFICATION_TARGET_ROUTE = "/temporary-items";
const LOAN_NOTIFICATION_TARGET_ROUTE = "/loan-transactions";
const LOW_STOCK_TARGET_ROUTE = "/items";

// Shared look for the rows inside the notification panel.
const NOTIF_ROW =
  "w-full text-left px-4 py-3 border-t border-[var(--border-subtle)] hover:bg-[var(--bg-surface-hover)] focus:outline-none transition-colors";
const NOTIF_SECTION =
  "px-4 py-2 eyebrow !text-[10px] border-b border-[var(--border-subtle)] bg-[var(--bg-base)]";

function Topbar({ onMenuClick }) {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [notifOpen, setNotifOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifData, setNotifData] = useState({
    total_count: 0,
    expired_count: 0,
    expiring_soon_count: 0,
    expired_items: [],
    expiring_soon_items: [],
  });
  const [loanNotifData, setLoanNotifData] = useState({
    total_count: 0,
    overdue: [],
    due_today: [],
    due_tomorrow: [],
  });
  const [lowStockData, setLowStockData] = useState({ total_count: 0, items: [] });

  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState("");

  const notifRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    fetch(`${API_BASE_URL}/api/auth/me/`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => setUser(data))
      .catch(() => setUser(null));
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!["admin", "superadmin"].includes(user?.role)) return;

    try {
      setNotifLoading(true);

      const token = localStorage.getItem("accessToken");
      const headers = { Authorization: `Bearer ${token}` };

      const [expiryRes, loanRes, lowStockRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/items/expiry-notifications/`, { headers }),
        fetch(`${API_BASE_URL}/api/loan-notifications/`, { headers }),
        fetch(`${API_BASE_URL}/api/items/low-stock-notifications/`, { headers }),
      ]);

      if (expiryRes.ok) {
        const data = await expiryRes.json();
        setNotifData(data);
      }
      if (loanRes.ok) {
        const data = await loanRes.json();
        setLoanNotifData(data);
      }
      if (lowStockRes.ok) {
        const data = await lowStockRes.json();
        setLowStockData(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setNotifLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (["admin", "superadmin"].includes(user?.role)) {
      fetchNotifications();
    }
  }, [user, fetchNotifications]);

  const handleTogglePush = async () => {
    setPushError("");
    setPushBusy(true);
    const token = localStorage.getItem("accessToken");
    try {
      if (pushEnabled) {
        await unsubscribeFromPush(token);
        setPushEnabled(false);
      } else {
        await subscribeToPush(token);
        setPushEnabled(true);
      }
    } catch (err) {
      setPushError(err.message || "Gagal mengubah status push notifikasi.");
    } finally {
      setPushBusy(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getNotificationTypeParam = (itemType) => {
    const normalized = String(itemType || "").toLowerCase();

    if (normalized.includes("production")) return "production";
    if (normalized.includes("booth")) return "booth";
    if (normalized.includes("gimmick")) return "gimmick";
    if (normalized.includes("normal")) return "normal";

    return "gimmick";
  };

  const handleNotificationClick = (item) => {
    const type = getNotificationTypeParam(item.item_type);
    const search = item.code || item.name || "";

    setNotifOpen(false);

    navigate(
      `${NOTIFICATION_TARGET_ROUTE}?type=${encodeURIComponent(
        type
      )}&search=${encodeURIComponent(search)}`
    );
  };

  const handleLoanNotificationClick = (loan) => {
    setNotifOpen(false);
    navigate(`${LOAN_NOTIFICATION_TARGET_ROUTE}?search=${encodeURIComponent(loan.reference_code)}`);
  };

  const totalNotifCount = notifData.total_count + loanNotifData.total_count + lowStockData.total_count;
  const hasUrgentLoan = loanNotifData.overdue.length > 0 || loanNotifData.due_today.length > 0;

  const renderNotificationItem = (item, status) => {
    const statusText =
      status === "expired"
        ? `Expired ${item.days_overdue} day(s) ago`
        : item.days_left === 0
          ? "Expires today"
          : `${item.days_left} day(s) left`;

    const badgeClass = status === "expired" ? "badge-danger" : "badge-warning";

    return (
      <button
        key={`${status}-${item.id}`}
        type="button"
        onClick={() => handleNotificationClick(item)}
        className={NOTIF_ROW}
      >
        <div className="text-sm font-medium text-strong">
          {item.code} - {item.name}
        </div>
        <div className="text-xs text-muted mt-1">
          {item.item_type} • exp: {item.date_limit}
        </div>
        <span className={`badge ${badgeClass} mt-2`}>{statusText}</span>
      </button>
    );
  };

  const isAdmin = ["admin", "superadmin"].includes(user?.role);

  return (
    <header className="tb">
      <div className="flex items-center gap-3 min-w-0">
        {/* Hamburger — opens the nav drawer on mobile/tablet */}
        <button
          type="button"
          onClick={onMenuClick}
          aria-label="Buka menu"
          className="topbar-icon-btn md:hidden shrink-0"
        >
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <div className="tb-crumb">
          <span className="tb-dot" aria-hidden="true" />
          <span className="hidden sm:inline">WORKSPACE</span>
          <span className="tb-crumb-sep hidden sm:inline">/</span>
          <span className="tb-crumb-cur">{pageTitleFor(location.pathname)}</span>
        </div>
      </div>

      {isAdmin && (
        <div className="relative" ref={notifRef}>
          <button
            type="button"
            onClick={() => {
              setNotifOpen((prev) => {
                const next = !prev;
                if (next) {
                  fetchNotifications();
                  if (isPushSupported()) {
                    getExistingSubscription().then((sub) => setPushEnabled(!!sub));
                  }
                }
                return next;
              });
            }}
            className="tb-btn"
            aria-expanded={notifOpen}
            title="Notifikasi"
          >
            <span className="hidden sm:inline">NOTIFIKASI</span>
            <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <span className={`tb-count ${totalNotifCount > 0 ? "is-alert" : ""} ${hasUrgentLoan ? "animate-pulse" : ""}`}>
              {totalNotifCount}
            </span>
          </button>

          {notifOpen && (
            <div
              className="absolute right-0 mt-3 w-[380px] max-w-[calc(100vw-2rem)] overflow-hidden glass-dropdown rounded-[10px] z-50 animate-scale-in origin-top-right"
              style={{ maxHeight: "calc(100vh - 96px)" }}
            >
              <div className="px-4 py-3 border-b border-[var(--border-subtle)] sticky top-0 z-10 bg-[var(--bg-surface)]">
                <div className="flex items-center justify-between gap-2">
                  <div className="eyebrow !text-[11px]">Notifikasi</div>
                  {isPushSupported() && (
                    <button
                      type="button"
                      onClick={handleTogglePush}
                      disabled={pushBusy}
                      className="btn-outline btn-sm"
                      title={pushEnabled ? "Matikan push notifikasi di browser ini" : "Aktifkan push notifikasi untuk approval baru"}
                    >
                      {pushBusy ? "..." : pushEnabled ? "Matikan Push" : "Aktifkan Push"}
                    </button>
                  )}
                </div>
                {pushError && (
                  <p className="text-[11px] mt-1" style={{ color: "var(--danger)" }}>{pushError}</p>
                )}
              </div>

              <div
                className="overflow-y-auto"
                style={{ maxHeight: "calc(100vh - 154px)" }}
              >
                {notifLoading ? (
                  <div className="px-4 py-4 text-sm text-muted">Loading...</div>
                ) : totalNotifCount === 0 ? (
                  <div className="px-4 py-4 text-sm text-muted">
                    Tidak ada notifikasi.
                  </div>
                ) : (
                  <div>
                    {/* Loan notifications */}
                    {loanNotifData.total_count > 0 && (
                      <div>
                        <div className={NOTIF_SECTION}>Peminjaman Barang</div>
                        {loanNotifData.overdue.map((loan) => (
                          <button
                            key={`overdue-${loan.id}`}
                            type="button"
                            onClick={() => handleLoanNotificationClick(loan)}
                            className={NOTIF_ROW}
                          >
                            <div className="text-sm font-medium text-strong">{loan.reference_code}</div>
                            <div className="text-xs text-muted mt-0.5">{loan.item_code} — PIC: {loan.pic_name}</div>
                            <span className="badge badge-danger mt-1.5">
                              Terlambat {loan.days_overdue} hari • exp: {loan.due_date}
                            </span>
                          </button>
                        ))}
                        {loanNotifData.due_today.map((loan) => (
                          <button
                            key={`today-${loan.id}`}
                            type="button"
                            onClick={() => handleLoanNotificationClick(loan)}
                            className={NOTIF_ROW}
                          >
                            <div className="text-sm font-medium text-strong">{loan.reference_code}</div>
                            <div className="text-xs text-muted mt-0.5">{loan.item_code} — PIC: {loan.pic_name}</div>
                            <span className="badge badge-danger mt-1.5">Jatuh tempo hari ini</span>
                          </button>
                        ))}
                        {loanNotifData.due_tomorrow.map((loan) => (
                          <button
                            key={`tomorrow-${loan.id}`}
                            type="button"
                            onClick={() => handleLoanNotificationClick(loan)}
                            className={NOTIF_ROW}
                          >
                            <div className="text-sm font-medium text-strong">{loan.reference_code}</div>
                            <div className="text-xs text-muted mt-0.5">{loan.item_code} — PIC: {loan.pic_name}</div>
                            <span className="badge badge-warning mt-1.5">Jatuh tempo besok</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Item expiry notifications */}
                    {notifData.total_count > 0 && (
                      <div>
                        <div className={NOTIF_SECTION}>Item Kadaluarsa</div>
                        {notifData.expired_items.map((item) =>
                          renderNotificationItem(item, "expired")
                        )}
                        {notifData.expiring_soon_items.map((item) =>
                          renderNotificationItem(item, "soon")
                        )}
                      </div>
                    )}

                    {/* Low stock notifications */}
                    {lowStockData.total_count > 0 && (
                      <div>
                        <div className={NOTIF_SECTION}>Stok Rendah — Komponen Panel</div>
                        {lowStockData.items.map((item) => (
                          <button
                            key={`lowstock-${item.id}`}
                            type="button"
                            onClick={() => {
                              setNotifOpen(false);
                              navigate(`${LOW_STOCK_TARGET_ROUTE}?search=${encodeURIComponent(item.code)}`);
                            }}
                            className={NOTIF_ROW}
                          >
                            <div className="text-sm font-medium text-strong">{item.code} — {item.name}</div>
                            <div className="text-xs text-muted mt-0.5">
                              Stok: {item.quantity_available} / Min: {item.minimum_stock}
                              {item.templates.length > 0 && ` • ${item.templates.join(", ")}`}
                            </div>
                            <span className="badge badge-warning mt-1.5">
                              Kurang {item.shortage} {item.shortage === 1 ? "unit" : "unit"}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </header>
  );
}

export default Topbar;
